import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useMemo } from "react";
import { useAuthUser } from "react-auth-kit";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  useDeleteDenomination,
  useDenomination,
  useDenominationHierarchy,
  useStructureTypes,
  useUpdateDenomination
} from "../api";
import { updateStructureSchema } from "../validation";

export const useStructureDetailForm = () => {
  const { id: denominationId, structureId } = useParams();
  const navigate = useNavigate();
  const auth = useAuthUser();

  // API Hooks
  const structureQuery = useDenomination({
    denominationId: structureId,
    config: {
      enabled: !!structureId
    }
  });

  // Form setup
  const {
    register,
    handleSubmit,
    formState: { errors, defaultValues },
    reset,
    watch,
    setValue,
    control
  } = useForm({
    defaultValues: {
      name: structureQuery.data?.result?.name || "",
      acronyms: structureQuery.data?.result?.acronyms || "",
      email: structureQuery.data?.result?.email || "",
      phone: structureQuery.data?.result?.phone || "",
      p_o_box: structureQuery.data?.result?.p_o_box || "",
      country_id: structureQuery.data?.result?.country_id || "",
      region_id: structureQuery.data?.result?.region_id || "",
      zone_id: structureQuery.data?.result?.zone_id || "",
      city_id: structureQuery.data?.result?.city_id || "",
      sub_city_id: structureQuery.data?.result?.sub_city_id || "",
      woreda_id: structureQuery.data?.result?.woreda_id || "",
      kebele: structureQuery.data?.result?.kebele || "",
      house_number: structureQuery.data?.result?.house_number || "",
      specific_location: structureQuery.data?.result?.specific_location || "",
      description: structureQuery.data?.result?.description || "",
      denomination_structure_type_id:
        structureQuery.data?.result?.denomination_structure_type_id || "",
      parent_id: structureQuery.data?.result?.parent_id || ""
    },
    resolver: yupResolver(updateStructureSchema)
  });

  const structureMutation = useUpdateDenomination({
    redirect: false
  });

  const structureDeleteMutation = useDeleteDenomination({
    redirect: false
  });

  const { data: structureTypesData, isLoading: isLoadingStructureTypes } =
    useStructureTypes({
      denominationId,
      config: {
        enabled: !!denominationId
      }
    });

  const { data: denominationsData, isLoading: isLoadingDenominations } =
    useDenominationHierarchy({
      denominationId,
      page: 1,
      pageSize: 100,
      config: {
        enabled: !!denominationId
      }
    });

  const { data: childStructuresData, isLoading: isLoadingChildStructures } =
    useDenominationHierarchy({
      denominationId: structureId,
      config: {
        enabled: !!structureId
      }
    });

  // Memoized data
  const userId = auth()?.id;
  const structureCreatorId =
    structureQuery.data?.result?.created_by?.id ||
    structureQuery.data?.result?.created_by;
  const isCreatedByUser = String(userId) === String(structureCreatorId);

  const structureTypeOptions = useMemo(() => {
    if (!structureTypesData?.result) return [];
    return structureTypesData.result.map(type => ({
      optionLabel: type.name,
      value: type.id
    }));
  }, [structureTypesData]);

  const parentStructureOptions = useMemo(() => {
    if (!denominationsData?.result?.data) return [];
    return denominationsData.result?.data?.map(denomination => ({
      optionLabel: denomination.name,
      value: denomination.id
    }));
  }, [denominationsData]);

  const childStructures = useMemo(() => {
    if (!structureQuery.data?.result) return [];
    return childStructuresData?.result?.data || [];
  }, [childStructuresData, structureQuery.data]);

  // Form submission handler
  const onSubmit = useCallback(
    values => {
      structureMutation.mutate(
        {
          denominationData: values,
          denominationId: structureId
        },
        {
          onSuccess: () => {
            navigate(-1);
          }
        }
      );
    },
    [structureMutation, structureId, navigate]
  );

  // Delete handler
  const handleDelete = useCallback(() => {
    structureDeleteMutation.mutate(
      {
        denominationId: structureId
      },
      {
        onSuccess: () => {
          navigate(`/denomination-dashboard/${denominationId}/structures`, {
            replace: true
          });
        }
      }
    );
  }, [structureDeleteMutation, structureId, denominationId, navigate]);

  // Effects for form initialization
  useEffect(() => {
    if (structureQuery.data?.result) {
      reset({
        name: structureQuery.data.result.name || "",
        acronyms: structureQuery.data.result.acronyms || "",
        email: structureQuery.data.result.email || "",
        phone: structureQuery.data.result.phone || "",
        p_o_box: structureQuery.data.result.p_o_box || "",
        country_id: structureQuery.data.result.country_id || "",
        region_id: structureQuery.data.result.region_id || "",
        zone_id: structureQuery.data.result.zone_id || "",
        city_id: structureQuery.data.result.city_id || "",
        sub_city_id: structureQuery.data.result.sub_city_id || "",
        woreda_id: structureQuery.data.result.woreda_id || "",
        kebele: structureQuery.data.result.kebele || "",
        house_number: structureQuery.data.result.house_number || "",
        specific_location: structureQuery.data.result.specific_location || "",
        description: structureQuery.data.result.description || "",
        denomination_structure_type_id:
          structureQuery.data.result.denomination_structure_type_id || "",
        parent_id: structureQuery.data.result.parent_id || ""
      });
    }
  }, [structureQuery.data, reset]);

  return {
    // Form methods
    register,
    handleSubmit,
    errors,
    defaultValues,
    reset,
    watch,
    setValue,
    control,

    // Data
    structureTypeOptions,
    parentStructureOptions,
    childStructures,
    isCreatedByUser,
    denominationId,
    structureId,

    // Loading states
    isLoadingStructureTypes,
    isLoadingDenominations,
    isLoadingChildStructures,

    // Queries
    structureQuery,

    // Mutations
    structureMutation,
    structureDeleteMutation,

    // Handlers
    onSubmit,
    handleDelete
  };
};
